import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import ClientForm from "./ClientForm";
import { clientService } from "../../services/client.service";
import { validateEmail, validatePhone, validateGSTIN, validatePincode } from "../../utils/formValidation";

export default function ClientDrawer({ client, onClose, onSaved }) {
  const [open, setOpen] = useState(true);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  const [form, setForm] = useState({
    name: "",
    contact_name: "",
    email: "",
    phone: "",
    gstin: "",
    pan: "",
    billing_address: "",
    city: "",
    state: "",
    pincode: "",
    country: "India",
    private_notes: "",
    other_notes: "",
  });

    // ─────────────────────────────────────
    // Close with animation (single source)
    // ─────────────────────────────────────
    function handleClose() {
      setOpen(false);
    }

    // ESC key (desktop UX)
    useEffect(() => {
      function onKey(e) {
        if (e.key === "Escape") handleClose();
      }
      window.addEventListener("keydown", onKey);
      return () => window.removeEventListener("keydown", onKey);
    }, []);

    useEffect(() => {
    if (client) {
      // EDIT mode → hydrate
      setForm({
        name: client.name || "",
        contact_name: client.contact_name || "",
        email: client.email || "",
        phone: client.phone || "",
        gstin: client.gstin || "",
        pan: client.pan || "",
        tin: client.tin || "",
        vat_no: client.vat_no || "",
        billing_address: client.billing_address || "",
        city: client.city || "",
        state: client.state || "",
        pincode: client.pincode || "",
        country: client.country || "India",
        private_notes: client.private_notes || "",
        other_notes: client.other_notes || "",
      });
    } else {
      // ADD mode → reset
      setForm({
        name: "",
        contact_name: "",
        email: "",
        phone: "",
        gstin: "",
        pan: "",
        tin: "",
        vat_no: "",
        billing_address: "",
        city: "",
        state: "",
        pincode: "",
        country: "India",
        private_notes: "",
        other_notes: "",
      });
    }
  }, [client]);


  // Validate field
  const validateField = (name, value) => {
    let error = null;

    switch (name) {
      case 'name':
        if (!value || value.trim() === '') {
          error = 'Client name is required';
        }
        break;

      case 'email':
        if (value && value.trim() !== '') {
          const validation = validateEmail(value);
          error = validation.error;
        }
        break;

      case 'phone':
        if (value && value.trim() !== '') {
          const validation = validatePhone(value);
          error = validation.error;
        }
        break;

      case 'gstin':
        if (value && value.trim() !== '') {
          const validation = validateGSTIN(value);
          error = validation.error;
        }
        break;

      case 'pincode':
        if (value && value.trim() !== '') {
          const validation = validatePincode(value);
          error = validation.error;
        }
        break;

      default:
        break;
    }

    setErrors(prev => ({
      ...prev,
      [name]: error
    }));

    return !error;
  };

  async function handleSubmit() {
    // Validate all fields before submit
    const fieldsToValidate = ['name', 'email', 'phone', 'gstin', 'pincode'];
    let isValid = true;
    const newErrors = {};

    fieldsToValidate.forEach(field => {
      const fieldValue = form[field];
      const hasError = !validateField(field, fieldValue);
      if (hasError) {
        isValid = false;
        newErrors[field] = errors[field];
      }
    });

    // Mark all fields as touched
    const allTouched = fieldsToValidate.reduce((acc, field) => {
      acc[field] = true;
      return acc;
    }, {});
    setTouched(allTouched);

    // If validation fails, stop here and keep drawer open
    if (!isValid) {
      // Scroll to first error
      const firstErrorField = Object.keys(newErrors)[0];
      const element = document.querySelector(`[name="${firstErrorField}"]`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setTimeout(() => element.focus(), 300);
      }
      return; // Don't close drawer, don't save
    }

    // Validation passed, proceed with save
    if (client?.id) {
      // ✅ EDIT MODE
      await clientService.update(client.id, form);
    } else {
      // ✅ ADD MODE
      await clientService.add(form);
    }

    onSaved?.();
    handleClose();
  }

    return (
      <AnimatePresence onExitComplete={onClose}>
        {open && (
          <motion.div
            className="fixed inset-0 z-40 flex"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
          >
            {/* Overlay */}
            <motion.div
              className="flex-1 bg-black/30"
              onClick={handleClose}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
            />

            {/* Drawer */}
            <motion.div
              initial={{ x: 420 }}
              animate={{ x: 0 }}
              exit={{ x: 420 }}
              transition={{
                type: "spring",
                stiffness: 260,
                damping: 30, // smoother exit
              }}
              className="
                w-[420px]
                h-screen
                bg-white
                border-l border-[var(--color-border)]
                flex flex-col
              "
            >
              {/* Header (fixed) */}
              <div className="px-5 py-4 border-b border-[var(--color-border)] shrink-0">
                <h2 className="text-lg font-semibold">
                  {client ? "Edit Client" : "Add Client"}
                </h2>
                <p className="text-xs text-[var(--color-text-muted)]">
                  Client & billing information
                </p>
              </div>

              {/* Scrollable content */}
              <div className="flex-1 min-h-0 overflow-y-auto px-5 py-4">
                <motion.div
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08 }}
                >
                  <ClientForm 
                    form={form} 
                    setForm={setForm}
                    errors={errors}
                    touched={touched}
                    validateField={validateField}
                    setTouched={setTouched}
                  />
                </motion.div>
              </div>

              {/* Footer (fixed) */}
              <div
                className="
                  shrink-0
                  px-5 py-4
                  border-t border-[var(--color-border)]
                  bg-white
                  flex justify-end gap-3
                "
              >
                <button
                  onClick={handleClose}
                  className="px-4 py-2 text-sm rounded-md hover:bg-[var(--color-bg-muted)]"
                >
                  Cancel
                </button>

                <button
                  onClick={handleSubmit}
                  className="px-4 py-2 text-sm rounded-md bg-[var(--color-primary)] text-white"
                >
                  Save Client
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    );
  }
